<template>
  <div class="id-scope booking-roster">
    <!-- date band + legend -->
    <div class="roster-head">
      <div class="col" style="gap:2px">
        <h3 class="t-h3">{{ $t('bookingList.classBooking') }} {{ $formatFullDate(classdate) }}</h3>
        <span class="t-cap">{{ classdate.toLocaleDateString('en-GB') }}</span>
      </div>
      <!-- legend -->
      <div class="row gap3 wrap" style="font-size:12.5px">
        <span class="row" style="gap:5px"><v-icon size="14" color="#3b82f6">mdi-circle-slice-8</v-icon><span class="muted">{{ $t('bookingList.legendTrial') }}</span></span>
        <span class="row" style="gap:5px"><v-icon size="14" color="#ec4899">mdi-circle-slice-8</v-icon><span class="muted">{{ $t('bookingList.legendPerSession') }}</span></span>
        <span class="row" style="gap:5px"><v-icon size="14" color="#10b981">mdi-check-circle</v-icon><span class="muted">{{ $t('bookingList.menuCheckedInBadge') }}</span></span>
        <span class="row" style="gap:5px"><v-icon size="14" color="#f59e0b">mdi-bell-ring</v-icon><span class="muted">{{ $t('bookingList.legendPayment') }}</span></span>
        <span class="row" style="gap:5px"><v-icon size="14" color="#64748b">mdi-eye-off</v-icon><span class="muted">{{ $t('bookingList.legendBlind') }}</span></span>
      </div>
    </div>

    <!-- loading skeleton -->
    <div v-if="loadingBooking" class="tt-row" style="overflow:hidden">
      <div v-for="i in 4" :key="i" style="min-width:188px;flex:0 0 188px">
        <div class="id-skel" style="height:48px;margin-bottom:6px"></div>
        <div v-for="j in 3" :key="j" class="id-skel" style="height:48px;margin-bottom:6px"></div>
      </div>
    </div>

    <!-- empty -->
    <div v-else-if="!bookingData || bookingData.length === 0" class="id-empty">
      <div class="mdi mdi-calendar-blank-outline"></div>
      <div style="margin-top:8px">{{ $t('bookingMgmt.noBooking') }}</div>
    </div>

    <!-- roster: one column per time-slot header, one pill per student cell -->
    <div v-else class="tt-grid-wrap id-fade-in" :key="classdate.getTime()">
      <div class="tt-row">
        <template v-for="(header, index) in bookingHeaders" :key="`booking-col-${index}`">
          <div v-if="header.key !== 'idx'" class="tt-col">
            <div class="tt-colhead">
              <div class="row" style="justify-content:space-between">
                <span class="strong row" style="gap:5px"><v-icon size="15">mdi-clock-outline</v-icon> {{ header.title }}</span>
              </div>
            </div>
            <div class="tt-cell">
              <template v-for="(item, rowIndex) in bookingData" :key="`${header.key}-${rowIndex}`">
                <div v-if="item[header.key] && typeof item[header.key] === 'object' && item[header.key].name"
                  class="tt-pill cell-clickable"
                  :class="{
                    checked: item[header.key].name && item[header.key].name.includes('(1)'),
                    trial: item[header.key].name && item[header.key].name.includes('(blue)'),
                    session: item[header.key].name && item[header.key].name.includes('(pink)'),
                  }"
                  @click="handleCellClick($event, item[header.key], header.key)">
                  <span class="tt-avatar" style="background:var(--c-primary)">
                    {{ cellSeq(header.key, rowIndex) }}
                  </span>
                  <div class="col" style="gap:0;flex:1;min-width:0">
                    <span class="strong" style="font-size:13.5px;white-space:normal;overflow-wrap:anywhere">{{ parseName(item[header.key]) }}</span>
                    <span v-if="parseWarningText(item[header.key])" class="t-cap" style="font-size:11px;white-space:normal;overflow-wrap:anywhere">{{ parseWarningText(item[header.key]) }}</span>
                  </div>
                  <div class="row" style="gap:3px">
                    <v-icon v-if="item[header.key].name && item[header.key].name.includes('(blind)')" size="15" color="#64748b" v-hint="$t('bookingList.blindTooltip')">mdi-eye-off</v-icon>
                    <v-icon v-if="item[header.key].name && item[header.key].name.includes('(pay)')" size="15" color="#f59e0b">mdi-bell-ring</v-icon>
                    <v-icon v-if="item[header.key].name && item[header.key].name.includes('(1)')" size="16" color="#10b981">mdi-check-circle</v-icon>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Quick action menu: anchored to the clicked pill -->
    <v-menu
      v-model="menuOpen"
      :target="menuTarget"
      location="bottom start"
      offset="8"
      :close-on-content-click="false"
      :persistent="true"
      transition="scale-transition"
      origin="top left"
    >
      <div class="popmenu quick-menu">
        <div class="popmenu-head">
          <span class="popmenu-name">{{ selectedStudentName || '—' }}</span>
          <span v-if="isCheckedIn" class="badge badge-success" style="height:20px;font-size:11px">
            <v-icon size="12">mdi-check-decagram</v-icon>{{ $t('bookingList.menuCheckedInBadge') }}
          </span>
        </div>

        <button class="popmenu-item" @click="onChooseCheckin">
          <span class="pm-ico"><v-icon size="18" :color="isCheckedIn ? '#b45309' : '#047857'">{{ isCheckedIn ? 'mdi-undo-variant' : 'mdi-check-bold' }}</v-icon></span>
          {{ isCheckedIn ? $t('bookingList.menuUndoCheckin') : $t('bookingList.menuCheckin') }}
        </button>

        <!-- แถวอีเวนต์ (v3) มีแค่ "เช็คชื่อ" — ย้ายคาบ/ยกเลิกใบสมัครทำที่หน้าอีเวนต์เท่านั้น
             (เจ้าของสั่ง: อีเวนต์จัดการที่หน้าอีเวนต์ที่เดียว) และสองเมนูนั้นทำงานกับ tbooking_group
             ซึ่งอีเวนต์ไม่มีแถวอยู่แล้ว — โชว์ไว้จะกดแล้วพังเงียบ ๆ -->
        <template v-if="!isEventRow">
          <div class="popmenu-divider"></div>

          <button class="popmenu-item" @click="onChooseEdit">
            <span class="pm-ico"><v-icon size="18" color="#6366f1">mdi-account-edit</v-icon></span>
            {{ $t('bookingList.menuEdit') }}
          </button>

          <div class="popmenu-divider"></div>

          <button class="popmenu-item" @click="onChooseCancelBooking">
            <span class="pm-ico"><v-icon size="18" color="#dc2626">mdi-calendar-remove</v-icon></span>
            {{ $t('bookingList.menuCancel') }}
          </button>
        </template>
        <div v-else class="popmenu-note">{{ $t('bookingList.eventRowNote') }}</div>
      </div>
    </v-menu>

    <!-- Check-in / Undo check-in confirmation -->
    <id-modal v-model="checkinConfirmDialog" size="sm" persistent
      :icon="isCheckedIn ? 'mdi-undo-variant' : 'mdi-check-circle'"
      :title="isCheckedIn ? $t('bookingList.menuUndoCheckin') : $t('bookingList.menuCheckin')">
      <p style="margin:0">{{ isCheckedIn
        ? $t('bookingMgmt.confirmUndoCheckin', { name: selectedStudentName })
        : $t('bookingMgmt.confirmCheckin', { name: selectedStudentName }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="checkinConfirmDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="confirmCheckin"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- Cancel-booking confirmation -->
    <id-modal v-model="cancelConfirmDialog" size="sm" persistent
      icon="mdi-calendar-remove" :title="$t('bookingList.menuCancel')">
      <p style="margin:0">{{ $t('bookingList.confirmCancelBooking', { name: selectedStudentName }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="cancelConfirmDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmCancelBooking"><span class="mdi mdi-calendar-remove"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- ชั้นที่สอง: ใบเรียนรายครั้งที่จ่ายเงินแล้ว — การยกเลิกจะลบใบซื้อลงประวัติด้วย
         กดยกเลิกในชั้นนี้ = ไม่มีอะไรถูกส่งไป server เลย (การจองและใบซื้อคงเดิม) -->
    <id-modal v-model="paidCancelDialog" size="sm" persistent
      icon="mdi-cash-refund" :title="$t('bookingMgmt.perSessionPaidCancelTitle')">
      <p style="margin:0">{{ $t('bookingMgmt.perSessionPaidCancelBody', { refer: (selectedStudent && selectedStudent.courserefer) || '' }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="paidCancelDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmPaidCancel"><span class="mdi mdi-calendar-remove"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      menuOpen: false,
      menuTarget: null,
      checkinConfirmDialog: false,
      cancelConfirmDialog: false,
      paidCancelDialog: false,
      selectedStudent: null,
      selectedSlotKey: '',
    }
  },
  props: {
    classdate: {
      type: Date,
      required: true,
    },
    bookingHeaders: {
      type: Array,
      required: false,
    },
    bookingData: {
      type: Array,
      required: false,
    },
    loadingBooking: {
      type: Boolean,
      required: false,
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown, true);
    document.addEventListener('keydown', this.onDocumentKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMousedown, true);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  computed: {
    ...mapGetters({
      token: 'getToken',
    }),
    selectedStudentName() {
      const s = this.selectedStudent;
      if (!s) return '';
      if (typeof s === 'string') return this.stripMarkers(s);
      if (typeof s === 'object' && s.name) return this.stripMarkers(s.name);
      return '';
    },
    // แถวอีเวนต์ v3 = ไม่มี reservationid แต่มี registrationid (backend ส่ง reservationid: null มาโดยตั้งใจ)
    isEventRow() {
      const s = this.selectedStudent;
      return !!s && !s.reservationid && !!s.registrationid;
    },
    isCheckedIn() {
      const s = this.selectedStudent;
      if (!s || typeof s !== 'object') return false;
      if (s.checkedin !== undefined && s.checkedin !== null) {
        return parseInt(s.checkedin) === 1;
      }
      if (typeof s.name === 'string' && s.name.includes('(1)')) return true;
      return false;
    },
  },
  methods: {
    isClickableCell(value) {
      return !!(value && typeof value === 'object');
    },
    onDocumentMousedown(event) {
      if (!this.menuOpen) return;
      const t = event.target;
      if (!t) return;
      if (t.closest && (t.closest('.cell-clickable') || t.closest('.quick-menu'))) return;
      this.menuOpen = false;
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.menuOpen) {
        this.menuOpen = false;
      }
    },
    handleCellClick(event, value, key) {
      if (typeof value === "number") return;
      if (!value) return;
      if (typeof value !== 'object') return;

      const td = event.currentTarget;
      const nameLabel = td && td.querySelector
        ? td.querySelector('label[name="col-center"]')
        : null;

      this.selectedStudent = value;
      this.selectedSlotKey = key;
      this.menuTarget = nameLabel || td;
      this.menuOpen = true;
    },
    onChooseEdit() {
      const value = this.selectedStudent;
      const key = this.selectedSlotKey;
      this.menuOpen = false;
      this.$nextTick(() => {
        this.$emit('student-clicked', value, key);
      });
    },
    onChooseCheckin() {
      this.menuOpen = false;
      this.$nextTick(() => { this.checkinConfirmDialog = true; });
    },
    onChooseCancelBooking() {
      this.menuOpen = false;
      this.$nextTick(() => { this.cancelConfirmDialog = true; });
    },
    async confirmCancelBooking() {
      this.cancelConfirmDialog = false;
      // ใบเรียนรายครั้งที่จ่ายเงินแล้ว → ต้องยืนยันชั้นที่สองก่อน (จะลบใบซื้อลงประวัติด้วย)
      if (this.selectedStudent && Number(this.selectedStudent.perSession) === 1 && Number(this.selectedStudent.pkgPaid) === 1) {
        this.$nextTick(() => { this.paidCancelDialog = true; });
        return;
      }
      await this.doCancelBookingRequest();
    },
    async confirmPaidCancel() {
      this.paidCancelDialog = false;
      await this.doCancelBookingRequest();
    },
    async doCancelBookingRequest() {
      this.$emit('onLoading', true);
      try {
        const response = await axios.post(this.baseURL + '/cancelBookingByAdmin', {
          reservationid: this.selectedStudent.reservationid,
          studentid: this.selectedStudent.studentid,
          // included when the roster cell carries it — the backend uses it to
          // return the session to the student's course quota
          courserefer: this.selectedStudent.courserefer,
        }, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        if (response.data && response.data.success) {
          this.$emit('onInfoHandler', response.data.message || this.$t('msg.reservationDeleted'));
          this.$emit('onUpdateDataSuccess');
        } else {
          this.$emit('onErrorHandler',
            (response.data && response.data.message) || this.$t('bookingList.cancelBookingFail'));
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$emit('onErrorHandler', error.response.data.message);
          this.$emit('onClickChangeState', 'login');
        } else {
          // 403 (ไม่มีสิทธิ์เมนู) ส่งข้อความไทยมาใน body — ถ้าโยน error.message ผู้ใช้จะเห็น
          // "Request failed with status code 403" แทนคำอธิบายว่าทำไมกดไม่ได้
          this.$emit('onErrorHandler',
            (error.response && error.response.data && error.response.data.message) || error.message);
        }
      } finally {
        this.$emit('onLoading', false);
      }
    },
    async confirmCheckin() {
      const wasCheckedIn = this.isCheckedIn;
      // อีเวนต์ v3 อยู่คนละตาราง — ไม่มี reservationid แต่มี registrationid แทน จึงต้องยิง
      // endpoint ของอีเวนต์เอง (endpoint ของคลาสกลุ่มจะหาแถวไม่เจอ) · ตัวเดียวกันทั้งเช็ค/ยกเลิกเช็ค
      const isEvent = !this.selectedStudent.reservationid && !!this.selectedStudent.registrationid;
      const endpoint = isEvent ? '/checkinEventRegistration'
        : (wasCheckedIn ? '/undoCheckinByAdmin' : '/checkinByAdmin');
      const body = isEvent
        ? { registrationid: this.selectedStudent.registrationid, checkedin: wasCheckedIn ? 0 : 1 }
        : { reservationid: this.selectedStudent.reservationid, studentid: this.selectedStudent.studentid };
      this.checkinConfirmDialog = false;
      this.$emit('onLoading', true);
      try {
        const response = await axios.post(this.baseURL + endpoint, body, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        if (response.data && response.data.success) {
          this.$emit('onUpdateDataSuccess');
        } else {
          this.$emit('onErrorHandler',
            (response.data && response.data.message) ||
            (wasCheckedIn ? this.$t('bookingList.undoCheckinFail') : this.$t('bookingList.checkinFail')));
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$emit('onErrorHandler', error.response.data.message);
          this.$emit('onClickChangeState', 'login');
        } else {
          // 403 (ไม่มีสิทธิ์เมนู) ส่งข้อความไทยมาใน body — ถ้าโยน error.message ผู้ใช้จะเห็น
          // "Request failed with status code 403" แทนคำอธิบายว่าทำไมกดไม่ได้
          this.$emit('onErrorHandler',
            (error.response && error.response.data && error.response.data.message) || error.message);
        }
      } finally {
        this.$emit('onLoading', false);
      }
    },
    stripMarkers(name) {
      if (typeof name !== 'string') return '';
      return name
        .replace('(1)', '')
        .replace('(red)', '')
        .replace('(green)', '')
        .replace('(blue)', '')
        .replace('(yellow)', '')
        .replace('(pink)', '')
        .replace('(pay)', '')
        .replace('(blind)', '')
        .trim();
    },
    SQLDate(date) {
      return moment(date).format('YYYY-MM-DD')
    },
    format_date(value) {
      if (value) {
        return moment(value).format('DD/MM/YYYY')
      }
    },
    highlightCell() {
      return 'highlighted-cell';
    },
    parseName(value) {
      //console.log('parseName value :', value);
      if (typeof value === 'object' && value !== null) {
        return value.name
          .replace('(1)', '')
          .replace('(red)', '')
          .replace('(green)', '')
          .replace('(blue)', '')
          .replace('(yellow)', '')
          .replace('(pink)', '')
          .replace('(pay)', '')
          .replace('(blind)', '');
      }
      return value;
    },
    // เลขลำดับของ cell ที่มีรายชื่อ นับเฉพาะแถวที่ไม่ว่างในคอลัมน์เดียวกัน (เริ่มที่ 1)
    cellSeq(headerKey, rowIndex) {
      let n = 0
      for (let i = 0; i <= rowIndex; i++) {
        const cell = this.bookingData[i] && this.bookingData[i][headerKey]
        if (cell && typeof cell === 'object' && cell.name) n++
      }
      return n
    },
    parseWarningText(value) {
      //console.log('parseWarningText value :', value);
      if (typeof value === 'object' && value !== null) {
        return value.msg;
      }
      return '';
    },
    getClass(value) {
      //console.log('getClass value :', value);
      const classes = [];
      
      if (typeof value === 'object' && value !== null) {
        const name = value.name;
        classes.push(
          name.includes('(1)') ? 'highlighted-blackground' : '',
          name.includes('(red)') ? 'highlighted-cell-red' : '',
          name.includes('(green)') ? 'highlighted-cell-green' : '',
          name.includes('(blue)') ? 'highlighted-cell-blue' : '',
          name.includes('(yellow)') ? 'highlighted-cell-yellow' : '',
          name.includes('(pink)') ? 'highlighted-cell-pink' : ''
        );
      } else if (value !== null && typeof value === 'number') {
        classes.push('bold-cell');
      }

      if (value !== undefined) {
        if (typeof value === 'number') {
          /* no-op */
        } else {
          classes.push('cell-nickname hover-cell');
        }
      }

      return classes;
    }
  },
};
</script>

<style scoped>
/* same floor as PrivateBookingList.vue's .private-booking-roster — keeps the two cards
   visually matched in size when either is empty, without capping growth when populated */
.booking-roster { padding: 4px; min-height: 300px; }
.roster-head { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px;
  padding: 4px 4px 14px; }
.booking-roster :deep(.v-overlay__content) { box-shadow: none; }
</style>